import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { SessionListItem, CombinedConfigResponse, mergeConfigs } from '@anchorbase/pillar-sdk';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { JsonEditor } from '@/components/config/JsonEditor';
import { sambaConfigApi } from '@/lib/pillarApi';

interface ActiveSessionsCardProps {
    sessions: SessionListItem[];
    isLoading?: boolean;
    error?: Error | null;
    onTerminateSession?: (session: SessionListItem) => void;
    isTerminating?: boolean;
    onToggleConsoleLogging?: (session: SessionListItem, enabled: boolean) => void;
    isTogglingConsole?: boolean;
    mode?: 'user' | 'admin';
    showSearch?: boolean;
    title?: string;
    description?: string;
}

function formatDate(isoString: string): string {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
        return 'Invalid date';
    }
    return date.toLocaleString();
}

function getTimeRemaining(expiresAt: string): string {
    const now = Date.now();
    const expiry = new Date(expiresAt).getTime();
    if (isNaN(expiry)) {
        return 'Invalid date';
    }
    const diff = expiry - now;
    if (diff <= 0) return 'Expired';
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) {
        return `${days}d ${hours % 24}h remaining`;
    }
    if (hours > 0) {
        return `${hours}h ${minutes % 60}m remaining`;
    }
    return `${minutes}m remaining`;
}

function formatActiveRoleAssignment(session: SessionListItem): string {
    if (
        session.active_role_assignment_uuid === null ||
        session.active_role_assignment_uuid === undefined
    ) {
        return 'None';
    }
    if (
        session.active_role_assignment_role === null ||
        session.active_role_assignment_role === undefined
    ) {
        return session.active_role_assignment_uuid;
    }
    if (
        session.active_role_assignment_organization_name !== null &&
        session.active_role_assignment_organization_name !== undefined
    ) {
        return (
            `${session.active_role_assignment_role} @ ` +
            session.active_role_assignment_organization_name
        );
    }
    return `${session.active_role_assignment_role} (internal)`;
}

export interface SessionCardProps {
    session: SessionListItem;
    mode: 'user' | 'admin';
    isCurrentSession?: boolean;
    onTerminate?: () => void;
    isTerminating: boolean;
    onToggleConsoleLogging?: (session: SessionListItem, enabled: boolean) => void;
    isTogglingConsole: boolean;
}

export function SessionCard({
    session,
    mode,
    isCurrentSession,
    onTerminate,
    isTerminating,
    onToggleConsoleLogging,
    isTogglingConsole,
}: SessionCardProps) {
    const isAdmin = mode === 'admin';
    const [isConfigExpanded, setIsConfigExpanded] = useState(false);

    let adminUserEmail: string | undefined;
    if (isAdmin) {
        if (!session.user_email) {
            throw new Error('Admin session is missing user_email');
        }
        adminUserEmail = session.user_email;
    }

    const sessionAuditId: number | undefined = session.audit_id;
    const sessionUuid: string | undefined = session.session_uuid;
    const activeRoleAssignmentLabel: string = formatActiveRoleAssignment(session);
    const shouldShowActiveRoleAssignmentUuid: boolean =
        session.active_role_assignment_uuid !== null &&
        session.active_role_assignment_uuid !== undefined &&
        activeRoleAssignmentLabel !== session.active_role_assignment_uuid;

    // Fetch config when expanded
    const { data: configData, isLoading: configLoading } = useQuery<
        CombinedConfigResponse & { effective_config: Record<string, unknown> }
    >({
        queryKey: ['sessionConfig', mode, isAdmin ? sessionUuid : sessionAuditId ?? sessionUuid ?? 'unknown'],
        queryFn: async () => {
            if (isAdmin) {
                if (sessionUuid === undefined) {
                    throw new Error('Session configuration is unavailable without session_uuid');
                }
                const combinedConfig =
                    await sambaConfigApi.getCombinedConfigForSessionOther(sessionUuid);
                const effectiveConfig = mergeConfigs(
                    combinedConfig.org_config,
                    combinedConfig.user_config
                );
                return {
                    ...combinedConfig,
                    effective_config: effectiveConfig,
                };
            }
            if (sessionAuditId === undefined) {
                throw new Error('Session configuration is unavailable without audit_id');
            }
            const combinedConfig = await sambaConfigApi.getCombinedConfigForSessionSelf(
                sessionAuditId
            );
            const effectiveConfig = mergeConfigs(
                combinedConfig.org_config,
                combinedConfig.user_config
            );
            return {
                ...combinedConfig,
                effective_config: effectiveConfig,
            };
        },
        enabled: isConfigExpanded && (isAdmin ? sessionUuid !== undefined : sessionAuditId !== undefined),
    });

    // Admin view with full details

    return (
        <div
            className={`border rounded-lg p-4 ${isCurrentSession ? 'border-blue-300 bg-blue-50' : ''}`}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        {isAdmin && (
                            <>
                                <h3 className="font-semibold">{adminUserEmail}</h3>
                            </>
                        )}
                        {isCurrentSession && <Badge variant="default">Your Session</Badge>}
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        <div>
                            <span className="font-medium">IP Address:</span> {session.ip_address}
                        </div>
                        <div>
                            <span className="font-medium">User Agent:</span> {session.user_agent}
                        </div>
                        <div>
                            <span className="font-medium">Expires:</span>{' '}
                            {formatDate(session.expires_at)}
                        </div>
                        <div>
                            <span className="font-medium">Status:</span>{' '}
                            <Badge variant="secondary" className="ml-1">
                                {getTimeRemaining(session.expires_at)}
                            </Badge>
                        </div>
                    </div>

                    {/* Active Role Assignment */}
                    <div className="pt-2 border-t">
                        <div className="space-y-1 text-sm text-muted-foreground">
                            <div>
                                <span className="font-medium">Active Role Assignment:</span>{' '}
                                <span className="text-foreground">{activeRoleAssignmentLabel}</span>
                            </div>
                            {shouldShowActiveRoleAssignmentUuid && (
                                <div>
                                    <span className="font-medium">Role Assignment UUID:</span>{' '}
                                    <code className="text-xs text-foreground">
                                        {session.active_role_assignment_uuid}
                                    </code>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="pt-2 border-t">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="font-medium">Console Logging:</span>
                            <span>
                                {session.enable_javascript_console ? 'Enabled' : 'Disabled'}
                            </span>
                            {isAdmin && onToggleConsoleLogging && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        onToggleConsoleLogging(
                                            session,
                                            !session.enable_javascript_console
                                        )
                                    }
                                    disabled={isTogglingConsole}
                                >
                                    {session.enable_javascript_console ? 'Disable' : 'Enable'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Collapsible Config Section */}
                    <div className="pt-2 border-t">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsConfigExpanded(!isConfigExpanded)}
                            className="w-full justify-between"
                            disabled={isAdmin ? sessionUuid === undefined : sessionAuditId === undefined}
                        >
                            <span className="text-sm font-medium">Session Configuration</span>
                            {isConfigExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                        </Button>
                        {isConfigExpanded && (
                            <div className="mt-4 space-y-4">
                                {configLoading ? (
                                    <p className="text-sm text-muted-foreground">
                                        Loading configuration...
                                    </p>
                                ) : configData ? (
                                    <>
                                        <div>
                                            <h4 className="text-sm font-semibold mb-2">
                                                Organization Configuration
                                            </h4>
                                            <JsonEditor
                                                value={configData.org_config}
                                                onChange={() => {}}
                                                disabled={true}
                                                placeholder="No organization configuration"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-semibold mb-2">
                                                User Configuration
                                            </h4>
                                            <JsonEditor
                                                value={configData.user_config}
                                                onChange={() => {}}
                                                disabled={true}
                                                placeholder="No user configuration"
                                            />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-semibold mb-2">
                                                Computed Configuration
                                            </h4>
                                            <JsonEditor
                                                value={configData.effective_config}
                                                onChange={() => {}}
                                                disabled={true}
                                                placeholder="No computed configuration"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        No configuration available
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {onTerminate && (
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={onTerminate}
                        disabled={isTerminating}
                    >
                        Terminate
                    </Button>
                )}
            </div>
        </div>
    );
}

export function ActiveSessionsCard({
    sessions,
    isLoading = false,
    error = null,
    onTerminateSession,
    isTerminating = false,
    onToggleConsoleLogging,
    isTogglingConsole = false,
    mode = 'user',
    showSearch = false,
    title = 'Active Sessions',
    description = 'Manage your active login sessions across different devices',
}: ActiveSessionsCardProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sessionToTerminate, setSessionToTerminate] = useState<SessionListItem | null>(null);
    const [showTerminateDialog, setShowTerminateDialog] = useState(false);

    const handleTerminateSession = (session: SessionListItem) => {
        setSessionToTerminate(session);
        setShowTerminateDialog(true);
    };

    const confirmTerminateSession = () => {
        if (sessionToTerminate && onTerminateSession) {
            onTerminateSession(sessionToTerminate);
            setShowTerminateDialog(false);
            setSessionToTerminate(null);
        }
    };

    // Filter sessions by search query (admin mode only)
    const filteredSessions: SessionListItem[] =
        showSearch && searchQuery.trim().length > 0
            ? sessions.filter((session) => {
                  const matchesEmail =
                      session.user_email &&
                      session.user_email.toLowerCase().includes(searchQuery.toLowerCase());
                  const matchesId =
                      session.user_id !== undefined &&
                      session.user_id.toString().includes(searchQuery);
                  const matchesSessionUuid =
                      session.session_uuid !== undefined &&
                      session.session_uuid.toLowerCase().includes(searchQuery.toLowerCase());
                  return matchesEmail || matchesId || matchesSessionUuid;
              })
            : sessions;

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {showSearch && (
                        <div className="mb-4">
                            <Input
                                placeholder="Search by user email or ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="max-w-md"
                            />
                        </div>
                    )}

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {error instanceof Error ? error.message : 'Failed to load sessions'}
                            </AlertDescription>
                        </Alert>
                    )}

                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">Loading sessions...</p>
                    ) : filteredSessions.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            {searchQuery && 'No sessions match your search'}
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {filteredSessions.map((session) => {
                                const isCurrentSession = session.is_current;
                                const sessionKey =
                                    session.session_uuid ??
                                    (session.audit_id !== undefined
                                        ? `audit:${session.audit_id}`
                                        : `${session.user_email}:${session.expires_at}`);
                                const canTerminate =
                                    onTerminateSession !== undefined && !isCurrentSession;
                                return (
                                    <SessionCard
                                        key={sessionKey}
                                        session={session}
                                        mode={mode}
                                        isCurrentSession={isCurrentSession}
                                        onTerminate={
                                            canTerminate
                                                ? () => handleTerminateSession(session)
                                                : undefined
                                        }
                                        isTerminating={isTerminating}
                                        onToggleConsoleLogging={onToggleConsoleLogging}
                                        isTogglingConsole={isTogglingConsole}
                                    />
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Terminate Session Confirmation Dialog */}
            <Dialog open={showTerminateDialog} onOpenChange={setShowTerminateDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>End Session</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to terminate this session?
                        </DialogDescription>
                    </DialogHeader>
                    {sessionToTerminate && (
                        <div className="py-4">
                            <div className="space-y-2 text-sm">
                                {mode === 'admin' && (
                                    <>
                                        <div>
                                            <span className="font-medium">User:</span>{' '}
                                            {sessionToTerminate.user_email}
                                        </div>
                                    </>
                                )}
                                {sessionToTerminate.session_uuid ? (
                                    <div>
                                        <span className="font-medium">Session:</span>{' '}
                                        <code className="text-xs">
                                            {sessionToTerminate.session_uuid}
                                        </code>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowTerminateDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmTerminateSession}
                            disabled={isTerminating}
                        >
                            {isTerminating ? 'Ending...' : 'End Session'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
